import { Box } from '@mui/material';
import { type JSX, useEffect, useState } from 'react';

import { DueDiligenceAssessmentConfigurationApi } from 'Api/TPRM/DueDiligenceAssessmentConfigurationApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { UcrmDueDiligenceAssessmentConfigurationApi } from 'Api/UCRM/UcrmDueDiligenceAssessmentConfigurationApi';
import { updateAssessmentSubject } from 'Api/UcrmTprm/UcrmTprmApi';
import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { IconButton } from 'Components/Buttons/IconButton';
import { FormFieldDatePicker } from 'Components/FormField/FormFieldDatePicker/FormFieldDatePicker';
import { Modal } from 'Components/Modal/Modal';
import { Text } from 'Components/Text/Text';
import { ICON_CLOSE, ICON_DELETE_REMOVE } from 'Config/Icons';
import { TPRM_DUE_DILIGENCE_WORKFLOW_DUE_DATE_WITHOUT_RECOMMENDATION, UCRM_DUE_DILIGENCE_WORKFLOW_DUE_DATE_WITHOUT_RECOMMENDATION } from 'Config/Tooltips';
import { iso8601ToJsDate, jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { getRecommendedReassessmentIntervalInMonths } from 'Helpers/UcrmTprm';
import { ReassessmentIntervals } from 'Models/DueDiligenceWorkflow';
import { ProductModule } from 'Models/ProductModule';
import { RiskRating, riskRatingAsString } from 'Models/RiskRating';
import { ThirdPartyService, UpdateServiceRequest } from 'Models/TPRM';
import { UseCase } from 'Models/Ucrm';

import styles from './DueDiligenceWorkflowDueDateModal.module.css';

interface DueDiligenceWorkflowDueDateModalBaseProps {
    hideModal: () => void;
    onDueDateSet: () => void;
}

/** `module` determines the types of the APIs and `assessmentSubject`, so that TPRM-only fields are included in the update request based on the module rather than on the shape of `assessmentSubject`. */
export type DueDiligenceWorkflowDueDateModalProps = DueDiligenceWorkflowDueDateModalBaseProps & ({ module: ProductModule.TPRM; assessmentSubject: ThirdPartyService; dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi; moduleApi: ClientTPRMApi } | { module: ProductModule.UCRM; assessmentSubject: UseCase; dueDiligenceAssessmentConfigurationApi: UcrmDueDiligenceAssessmentConfigurationApi; moduleApi: ClientUcrmApi });

interface FormState {
    isLoading?: boolean;
    successMessage?: string;
    failureMessage?: string;
}

export const DueDiligenceWorkflowDueDateModal = (props: DueDiligenceWorkflowDueDateModalProps): JSX.Element => {
    const [formState, setFormState] = useState<FormState>({});
    const [dueDate, setDueDate] = useState<Date | undefined>(props.assessmentSubject.assessment_workflow_setup.due_date ? iso8601ToJsDate(props.assessmentSubject.assessment_workflow_setup.due_date) : undefined);
    const [reassessmentIntervals, setReassessmentIntervals] = useState<ReassessmentIntervals>();

    const inherentRiskRating: RiskRating = props.assessmentSubject.inherent_risk_score ?? RiskRating.UNRATED;
    // Calculated from the configured timeframes when the modal opens, rather than stored with the assessment subject, so that it reflects the current configuration.
    const reassessmentIntervalInMonths = reassessmentIntervals ? getRecommendedReassessmentIntervalInMonths(inherentRiskRating, reassessmentIntervals) : undefined;

    useEffect(() => {
        const getTimeframes = async (): Promise<void> => {
            try {
                const response = await props.dueDiligenceAssessmentConfigurationApi.getTimeframes();
                setReassessmentIntervals(response.data.service_reassessment_intervals);
            } catch (error) {
                setFormState({ failureMessage: error.message });
            }
        };

        getTimeframes();
    }, [props.dueDiligenceAssessmentConfigurationApi]);

    const handleChangeDate = (date: Date): void => {
        setDueDate(date);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();
        try {
            setFormState({ isLoading: true });
            const request: UpdateServiceRequest = {
                name: props.assessmentSubject.name,
                description: props.assessmentSubject.description,
                vendor_service_manager_user_id: props.assessmentSubject.vendor_service_manager_user_id,
                assessment_due_date: dueDate ? jsDateToIso8601(dueDate) : null,
                delegates: props.assessmentSubject.delegates,
                irq_target_completion_date: props.assessmentSubject.assessment_workflow_setup.irq_target_completion_date,
                tags: props.assessmentSubject.tags,
                // Use cases (UCRM) do not have a responsible organization or contacts.
                ...(props.module === ProductModule.TPRM ? { responsible_organization: props.assessmentSubject.responsible_organization, vendor_contacts: props.assessmentSubject.vendor_contacts } : {}),
            };
            await updateAssessmentSubject(props.moduleApi, request, props.assessmentSubject.vendor_id, props.assessmentSubject.id);
            props.onDueDateSet();
            setFormState({ successMessage: 'Due date saved.', isLoading: false });
        } catch (error) {
            handleRequestError(error);
        }
    };

    const handleRequestError = (error: Error) => {
        setFormState({ failureMessage: error.message, isLoading: false });
    };

    return (
        <Modal open header="Schedule Due Diligence Workflow">
            {formState.successMessage && <Alert type="success">{formState.successMessage}</Alert>}
            {formState.failureMessage && <Alert type="error">{formState.failureMessage}</Alert>}
            <Box component="form" noValidate onSubmit={handleSubmit}>
                {reassessmentIntervalInMonths !== undefined && (
                    <div>
                        <Text>
                            Based on a {riskRatingAsString(inherentRiskRating)} inherent risk rating, the frequency for conducting a due diligence workflow for {props.assessmentSubject.name} is every {reassessmentIntervalInMonths} month(s).
                        </Text>
                    </div>
                )}
                <div className={styles.datePicker}>
                    <FormFieldDatePicker dateFormat="MM/dd/yyyy" selected={dueDate} handleChange={handleChangeDate} formFieldId="dueDate" formFieldLabel="Due Date" placeholder={'MM/DD/YYYY'} tooltip={props.module === ProductModule.UCRM ? UCRM_DUE_DILIGENCE_WORKFLOW_DUE_DATE_WITHOUT_RECOMMENDATION : TPRM_DUE_DILIGENCE_WORKFLOW_DUE_DATE_WITHOUT_RECOMMENDATION} />
                    <div className={styles.clearIcon}>
                        <IconButton aria-label="clear-date" onClick={() => setDueDate(undefined)} fontAwesomeImage={ICON_DELETE_REMOVE} />
                    </div>
                </div>

                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                        Close
                    </Button>
                    <Button variant="submit" isLoading={formState.isLoading === true} loadingText="Saving...">
                        Save
                    </Button>
                </div>
            </Box>
        </Modal>
    );
};
